import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, message, Space, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FileExcelOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import DataTable, { type DataTableRef } from '../../components/Charts/DataTable';
import { ForgeQueryBuilder, type FieldConfig, type ForgeQuery } from '../../components/ForgeQueryBuilder';
import { installmentApi, type InstallmentLedgerListRequest, type InstallmentLedgerSummary, type RepaymentLedgerJoined } from '../../services/statistics/installment';
import { useShopMap } from '../../services/store/organization.store';
import { OrderTypeMap } from '../../types/process/order';
import { ORDER_STATUS_LABEL_MAP, getOrderStatusLabel } from '../../types/process/order-data';
import { useLocation } from 'react-router-dom';

const { Text, Title } = Typography;

const emptySummary: InstallmentLedgerSummary = {
  totalCount: 0,
  billAmountTotal: 0,
  actualPaymentTotal: 0,
  cashflowReceivedTotal: 0,
  settlementRentTotal: 0,
  paymentDiscountTotal: 0,
  overdueHandledAmountTotal: 0,
};

const installmentLedgerExportExcludedKeys = [
  'originRent',
  'rentalNeedPay',
  'firstRent',
  'installmentPrice',
  'depositValue',
  'productValue',
  'buyoutPrice',
  'refundAmount',
  'alreadyFrozen',
  'hadPayRental',
  'expectedRecoveryAmount',
];

const installmentLedgerExportGroupHeaders = [
  { title: '分期金额', children: ['billAmount', 'paymentDiscount', 'actualPayment', 'settlementRent', 'overdueHandledAmount'] },
];

const formatCurrency = (value?: string | number): string => {
  const num = Number(value);
  if (!Number.isFinite(num)) {
    return '-';
  }
  return `¥${num.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatDate = (value?: string): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }
  return date.toLocaleDateString('sv-SE', { timeZone: 'Asia/Shanghai' });
};

const parseForgeQueryFromSearch = (search: string): ForgeQuery | undefined => {
  const searchParams = new URLSearchParams(search);
  const value = searchParams.get('q');
  if (!value) return undefined;
  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === 'object') return parsed;
  } catch {
    try {
      const parsed = JSON.parse(decodeURIComponent(value));
      if (parsed && typeof parsed === 'object') return parsed;
    } catch {
      return undefined;
    }
  }
  return undefined;
};

const mergeQueryFilters = (base?: Record<string, any>, override?: Record<string, any>): Record<string, any> => {
  const result: Record<string, any> = { ...(base || {}) };
  Object.entries(override || {}).forEach(([key, value]) => {
    const current = result[key];
    if (
      current &&
      value &&
      typeof current === 'object' &&
      typeof value === 'object' &&
      !Array.isArray(current) &&
      !Array.isArray(value) &&
      !('operator' in current) &&
      !('operator' in value)
    ) {
      result[key] = mergeQueryFilters(current, value);
      return;
    }
    result[key] = value;
  });
  return result;
};

const DROPPED_HIDDEN_FILTER_KEYS = new Set([
  'cashflow_approved_order_status',
  'cashflow_receivable_order_status',
]);

const stripDroppedHiddenFilters = (filter: Record<string, any> | undefined): Record<string, any> | undefined => {
  if (!filter || typeof filter !== 'object') {
    return filter;
  }

  const next = { ...filter };
  DROPPED_HIDDEN_FILTER_KEYS.forEach((key) => {
    delete next[key];
  });
  return next;
};

const collectHiddenFilters = (
  filter: Record<string, any> | undefined,
  visibleFieldKeys: Set<string>,
): Record<string, any> => {
  if (!filter || typeof filter !== 'object') {
    return {};
  }

  const hidden: Record<string, any> = {};
  Object.entries(filter).forEach(([key, value]) => {
    if (DROPPED_HIDDEN_FILTER_KEYS.has(key)) {
      return;
    }
    if (visibleFieldKeys.has(key)) {
      return;
    }
    hidden[key] = value;
  });
  return hidden;
};

const InstallmentLedger: React.FC = () => {
  const location = useLocation();
  const { shopList, shopMap } = useShopMap();
  const tableRef = useRef<DataTableRef>(null);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [rows, setRows] = useState<RepaymentLedgerJoined[]>([]);
  const [summary, setSummary] = useState<InstallmentLedgerSummary>(emptySummary);
  const [pageInfo, setPageInfo] = useState({ page: 1, pageSize: 20, total: 0 });
  const initialForcedQuery = useMemo(() => {
    const parsed = parseForgeQueryFromSearch(location.search) || {};
    return {
      ...parsed,
      filter: stripDroppedHiddenFilters(parsed.filter as Record<string, any> | undefined),
    };
  }, [location.search]);

  const queryFields: FieldConfig[] = useMemo(() => [
    { key: 'rentalStartDate', label: '账单日期', type: 'date', operators: ['between'], format: 'YYYY-MM-DD', placeholder: ['开始日期', '结束日期'] },
    { key: 'expectedDeductionDate', label: '应扣日期', type: 'date', operators: ['between'], format: 'YYYY-MM-DD', placeholder: ['开始日期', '结束日期'] },
    { key: 'actualPaymentDate', label: '实付日期', type: 'date', operators: ['between'], format: 'YYYY-MM-DD', placeholder: ['开始日期', '结束日期'] },
    { key: 'settlementTime', label: '结算时间', type: 'date', operators: ['between'], format: 'YYYY-MM-DD', placeholder: ['开始时间', '结束时间'] },
    {
      key: 'storeId', label: '店铺', type: 'select', operators: ['eq'],
      options: shopList.map(shop => ({ label: `${shop.name}`, value: Number(shop.code) }))
    },
    {
      key: 'order_type',
      label: '订单类型',
      type: 'select',
      operators: ['eq'],
      options: Object.entries(OrderTypeMap).map(([value, label]) => ({ label, value: Number(value) })),
    },
    {
      key: 'rrzu_status',
      label: '订单状态',
      type: 'multiSelect',
      operators: ['in'],
      width: 240,
      options: Object.entries(ORDER_STATUS_LABEL_MAP).map(([value, label]) => ({ label, value: Number(value) })),
    },
    { key: 'orderNo', label: '订单ID', type: 'string', operators: ['like'] },
    { key: 'rentBillId', label: '账单ID', type: 'string', operators: ['like'] },
    {
      key: 'periodNumber', label: '当期期数', type: 'select', operators: ['eq'],
      options: Array.from({ length: 23 }, (_, i) => ({ label: `M${i + 1}`, value: i + 2 }))
    },
    {
      key: 'appType', label: '订单渠道', type: 'select', operators: ['in'],
      options: [
        { label: '人人租app', value: '人人租app' },
        { label: '用户端APP', value: '用户端APP' },
        { label: '个人版小程序', value: '个人版小程序' },
        { label: '支付宝小程序', value: '支付宝小程序' },
        { label: '微信小程序', value: '微信小程序' },
      ]
    },
    { key: 'paymentMethod', label: '支付方式', type: 'string', operators: ['like'] },
    {
      key: 'cashflow_received_rent_type',
      label: '现金流租金分类',
      type: 'select',
      operators: ['eq'],
      options: [
        { label: '2.1.1 基础租金', value: 'basic_rent' },
        { label: '2.1.2 预收租金', value: 'prepaid_rent' },
        { label: '2.1.3 逾期回收款', value: 'overdue_recovery' },
        { label: '2.1.4 坏账追回款', value: 'bad_debt_recovery' },
      ],
    },
    {
      key: 'paymentStatus',
      label: '支付状态',
      type: 'multiSelect',
      operators: ['in', 'nin'],
      options: [
        { label: '支付完成', value: '支付完成' },
        { label: '支付完成(等待)', value: '支付完成(等待)' },
        { label: '未支付', value: '未支付' },
        { label: '处理中', value: '处理中' },
        { label: '已退款', value: '已退款' },
      ],
    },
    { key: 'isPeriodOverdued', label: '是否逾期', type: 'boolean', operators: ['eq'] },
    // { key: 'isPeriodOverdued', label: '已逾期', type: 'boolean', operators: ['eq'] },
    { key: 'isBadDebt', label: '是否坏账', type: 'boolean', operators: ['eq'] },
  ], [shopList]);

  const forcedHiddenFilters = useMemo(() => {
    const visibleFieldKeys = new Set(queryFields.map((field) => field.group ? `${field.group}.${field.key}` : field.key));
    return collectHiddenFilters(initialForcedQuery.filter as Record<string, any> | undefined, visibleFieldKeys);
  }, [initialForcedQuery.filter, queryFields]);

  const applyForcedHiddenFilters = useCallback((query: ForgeQuery): ForgeQuery => {
    const nextFilter = mergeQueryFilters(forcedHiddenFilters, stripDroppedHiddenFilters(query.filter as Record<string, any> | undefined));
    return {
      ...query,
      filter: nextFilter,
    };
  }, [forcedHiddenFilters]);

  const [lastQuery, setLastQuery] = useState<ForgeQuery>(() => applyForcedHiddenFilters(initialForcedQuery));

  const columns: ColumnsType<RepaymentLedgerJoined> = [
    {
      title: '账单ID', key: 'rentBillId', width: 100, fixed: 'left',
      render: (_: any, rec) => <Text copyable={{ text: rec.installmentDetail.rentBillId }} style={{ fontSize: 12 }}>{rec.installmentDetail.rentBillId}</Text>
    },
    {
      title: '订单ID', key: 'orderNo', width: 150,
      render: (_: any, rec) => <Text copyable={{ text: rec.installmentDetail.orderNo }} style={{ fontSize: 12 }}>{rec.installmentDetail.orderNo}</Text>
    },
    {
      title: '店铺', key: 'storeId', width: 60,
      render: (_: any, rec) => shopMap.get?.(Number(rec.installmentDetail.storeId))?.name || '-'
    },
    {
      title: '订单状态', key: 'rrzuStatus', width: 90,
      render: (_: any, rec) => getOrderStatusLabel(rec.orderData?.orderStatus)
    },
    {
      title: '订单渠道', key: 'appType', width: 120,
      render: (_: any, rec) => rec.installmentDetail.appType || '-'
    },
    {
      title: 'paymentMethod', key: 'paymentMethod', width: 120,
      render: (_: any, rec) => rec.installmentDetail.paymentMethod || '-'
    },
    {
      title: '当期期数', key: 'periodNumber', width: 60,
      render: (_: any, rec) => <Tag color="blue">M{rec.installmentDetail.periodNumber - 1}</Tag>
    },
    {
      title: '总期数', key: 'totalPeriods', width: 40,
      render: (_: any, rec) => rec.installmentDetail.totalPeriods ?? '-'
    },
    {
      title: '账单租期', key: 'rentalPeriod', width: 120,
      render: (_: any, rec) => {
        const startDate = rec.installmentDetail.rentalStartDate ? dayjs(rec.installmentDetail.rentalStartDate) : null;
        const endDate = rec.installmentDetail.rentalEndDate ? dayjs(rec.installmentDetail.rentalEndDate) : null;
        if (startDate && endDate) {
          return (
            <Text style={{ fontSize: '12px' }}>
              {startDate.format('YYYY-MM-DD')} 至 {endDate.format('YYYY-MM-DD')}
            </Text>
          );
        }
        return <Text style={{ fontSize: '12px' }}>-</Text>;
      },
    },
    {
      title: '应扣日期', key: 'expectedDeductionDate', width: 120,
      render: (_: any, rec) => formatDate(rec.installmentDetail.expectedDeductionDate)
    },
    {
      title: '实付日期', key: 'actualPaymentDate', width: 120,
      render: (_: any, rec) => formatDate(rec.installmentDetail.actualPaymentDate)
    },
    {
      title: '首期实付日期', key: 'firstPeriodPayDate', width: 130,
      render: (_: any, rec) => formatDate(rec.firstPeriodPayDate)
    },
    {
      title: '支付状态', key: 'paymentStatus', width: 80,
      render: (_: any, rec) => rec.installmentDetail.paymentStatus || '-'
    },
    {
      title: '与首期差异天数', key: 'daysFromFirstPay', width: 60,
      render: (_: any, rec) => rec.daysFromFirstPay != null ? rec.daysFromFirstPay : '-'
    },
    {
      title: '是否预收', key: 'isPrepaidRent', width: 50,
      render: (_: any, rec) => {
        if (rec.isPrepaidRent == null) return <Tag>-</Tag>;
        return <Tag color={rec.isPrepaidRent ? 'purple' : 'default'}>{rec.isPrepaidRent ? '是' : '否'}</Tag>;
      }
    },
    {
      title: '是否逾期', key: 'isPeriodOverdued', width: 50,
      render: (_: any, rec) => {
        const v = rec.installmentDetail.isPeriodOverdued;
        return <Tag color={v ? 'orange' : 'green'}>{v ? '是' : '否'}</Tag>;
      }
    },
    // {
    //   title: '已逾期', key: 'isPeriodOverdued', width: 90,
    //   render: (_: any, rec) => {
    //     const v = rec.installmentDetail.isPeriodOverdued;
    //     return <Tag color={v ? 'red' : 'green'}>{v ? '是' : '否'}</Tag>;
    //   }
    // },
    {
      title: '是否坏账', key: 'isBadDebt', width: 50,
      render: (_: any, rec) => {
        const v = rec.installmentDetail.isBadDebt;
        return <Tag color={v ? 'red' : 'green'}>{v ? '是' : '否'}</Tag>;
      }
    },
    {
      title: '账单金额', key: 'billAmount', width: 80,
      render: (_: any, rec) => <Text>{formatCurrency(rec.installmentDetail.billAmount)}</Text>
    },
    {
      title: '优惠金额', key: 'paymentDiscount', width: 80,
      render: (_: any, rec) => <Text>{formatCurrency(rec.installmentDetail.paymentDiscount)}</Text>
    },
    {
      title: '实付金额', key: 'actualPayment', width: 80,
      render: (_: any, rec) => <Text>{formatCurrency(rec.installmentDetail.actualPayment)}</Text>
    },
    {
      title: '结算时间', key: 'settlementTime', width: 160,
      render: (_: any, rec) => rec.installmentDetail.settlementTime ? dayjs(rec.installmentDetail.settlementTime).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
    {
      title: '结算租金', key: 'settlementRent', width: 120,
      render: (_: any, rec) => <Text>{formatCurrency(rec.installmentDetail.settlementRent)}</Text>
    },
  ];

  const fetchList = useCallback(async (query: ForgeQuery, page: number, pageSize: number) => {
    setLoading(true);
    try {
      const params = { ...query, page, pageSize } as InstallmentLedgerListRequest;
      const resp = await installmentApi.getInstallmentLedgerList(params);
      const data = resp.data;
      setRows(data?.items || []);
      if (data?.pagination) {
        setPageInfo({
          page: data.pagination.page,
          pageSize: data.pagination.page_size,
          total: data.pagination.total
        });
      } else {
        setPageInfo(prev => ({ ...prev, page, pageSize }));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSummary = useCallback(async (query: ForgeQuery) => {
    setSummaryLoading(true);
    try {
      const params = { ...query, page: 1, pageSize: 1 } as InstallmentLedgerListRequest;
      const resp = await installmentApi.getInstallmentLedgerSummary(params);
      setSummary(resp.data || emptySummary);
    } catch (error) {
      console.error('获取分期账单汇总失败:', error);
      message.error('获取汇总失败');
      setSummary(emptySummary);
    } finally {
      setSummaryLoading(false);
    }
  }, []);

  const handleSearch = async (query: ForgeQuery) => {
    const nextQuery = applyForcedHiddenFilters(query);
    setLastQuery(nextQuery);
    const nextPage = 1;
    setPageInfo(prev => ({ ...prev, page: nextPage }));
    await Promise.all([
      fetchList(nextQuery, nextPage, pageInfo.pageSize),
      fetchSummary(nextQuery),
    ]);
  };

  useEffect(() => {
    const nextQuery = applyForcedHiddenFilters(initialForcedQuery);
    setLastQuery(nextQuery);
    Promise.all([
      fetchList(nextQuery, 1, pageInfo.pageSize),
      fetchSummary(nextQuery),
    ]);
  }, [fetchList, fetchSummary, pageInfo.pageSize, initialForcedQuery, applyForcedHiddenFilters]);

  const handlePageChange = (page: number, pageSize?: number) => {
    const nextSize = pageSize || pageInfo.pageSize;
    const nextPage = nextSize !== pageInfo.pageSize ? 1 : page;
    setPageInfo(prev => ({ ...prev, page: nextPage, pageSize: nextSize }));
    fetchList(lastQuery, nextPage, nextSize);
  };

  const handleExportAll = useCallback(async () => {
    setExporting(true);
    try {
      const pageSize = 1000;
      let page = 1;
      let totalPages = 1;
      let total = 0;
      const allRows: RepaymentLedgerJoined[] = [];

      while (page <= totalPages) {
        const params = { ...lastQuery, page, pageSize } as InstallmentLedgerListRequest;
        const resp = await installmentApi.getInstallmentLedgerList(params);
        const data = resp.data;
        const items = data?.items || [];
        const pagination = data?.pagination;

        allRows.push(...items);
        total = pagination?.total ?? allRows.length;
        totalPages = Math.max(pagination?.total_pages ?? Math.ceil(total / pageSize), 1);

        if (items.length === 0 || allRows.length >= total) {
          break;
        }
        page += 1;
      }

      tableRef.current?.exportCSV({
        filename: 'installment-ledger.csv',
        rows: allRows,
        excludeKeys: installmentLedgerExportExcludedKeys,
        exportGroupHeaders: installmentLedgerExportGroupHeaders,
      });
      message.success(`导出成功，共 ${allRows.length} 条记录`);
    } catch (error) {
      console.error('导出账单明细失败:', error);
      message.error('导出失败，请稍后重试');
    } finally {
      setExporting(false);
    }
  }, [lastQuery]);

  const groupHeaders = [
    { title: '分期金额', children: ['billAmount', 'paymentDiscount', 'actualPayment', 'settlementRent', 'overdueHandledAmount'] },
    { title: '订单金额', children: ['originRent', 'rentalNeedPay', 'firstRent', 'installmentPrice', 'depositValue', 'productValue', 'buyoutPrice', 'refundAmount', 'alreadyFrozen', 'hadPayRental', 'expectedRecoveryAmount'] }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={2}>分期账单明细</Title>
      <div style={{ marginBottom: 12 }}>
        <ForgeQueryBuilder fields={queryFields} defaultQuery={initialForcedQuery} onSearch={handleSearch} />
      </div>
      <Card
        title="筛选汇总"
        loading={summaryLoading}
        style={{ marginBottom: 12 }}
      >
        <Space size={24} wrap>
          <Text>账单数：{summary.totalCount}</Text>
          <Text>账单金额：{formatCurrency(summary.billAmountTotal)}</Text>
          <Text>优惠金额：{formatCurrency(summary.paymentDiscountTotal)}</Text>
          <Text>实付金额：{formatCurrency(summary.actualPaymentTotal)}</Text>
          <Text>结算租金：{formatCurrency(summary.settlementRentTotal)}</Text>
        </Space>
      </Card>
      <Card
        title="分期账单明细列表"
        extra={
          <Space>
            <Button icon={<FileExcelOutlined />} loading={exporting} onClick={handleExportAll}>导出Excel</Button>
          </Space>
        }
      >
        <DataTable
          ref={tableRef as any}
          loading={loading}
          columns={columns}
          dataSource={rows as any}
          groupHeaders={groupHeaders as any}
          rowKey={(r: RepaymentLedgerJoined) => String(r.installmentDetail?.rentBillId || r.installmentDetail?.orderNo)}
          pagination={{
            current: pageInfo.page,
            pageSize: pageInfo.pageSize,
            total: pageInfo.total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条记录`,
            onChange: handlePageChange
          }}
        />
      </Card>
    </div>
  );
};

export default InstallmentLedger;
